import { FeedbackSurvey, FeedbackSurveyResult } from '@clickguard/design-system'
import { EligibleSurvey, SurveyDisplayScope } from 'api-models'
import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Surveys } from 'services/survey/surveys'
import { useAccountStore } from 'store/account-store'
import { useUserStore } from 'store/user-store'

/** Does the current pathname match this survey's page_url (trailing-segment match)? */
const matchesPage = (survey: EligibleSurvey, pathname: string): boolean => {
	const current = pathname.replace(/\/+$/, '')
	return current.endsWith(survey.page_url.replace(/^\/+/, ''))
}

/**
 * Choose which survey to show on the current page. A page-specific survey
 * targeting this page always wins; a generic (display-anywhere) survey is only
 * the fallback when no page-specific survey matches here. `eligible` is already
 * newest-first, so within each group the newest is chosen.
 */
const pickSurvey = (
	eligible: EligibleSurvey[],
	pathname: string
): EligibleSurvey | null => {
	const specific = eligible.find(
		(survey) =>
			survey.display_scope === SurveyDisplayScope.SPECIFIC_PAGE &&
			matchesPage(survey, pathname)
	)
	if (specific) {
		return specific
	}
	return (
		eligible.find(
			(survey) => survey.display_scope === SurveyDisplayScope.ANYWHERE
		) || null
	)
}

/**
 * App-wide host for the reusable feedback survey (DEV-2363). On each navigation it
 * asks the backend which surveys the user is eligible for, then renders at most one
 * `FeedbackSurvey` gated by the survey's display scope. The card is always
 * URL-gated, so it can never appear on the wrong page. Eligibility (triggers,
 * one-shot, cool-off) is decided server-side.
 */
export const SurveyHost = () => {
	const location = useLocation()
	const user = useUserStore((state) => state.user)
	const account = useAccountStore((state) => state.account)

	const [active, setActive] = useState<EligibleSurvey | null>(null)
	const impressionSent = useRef<string | null>(null)

	// Re-check eligibility on navigation. Never runs on admin pages.
	useEffect(() => {
		let cancelled = false
		const path = location.pathname

		if (!user || path.startsWith('/admin')) {
			setActive(null)
			return
		}

		;(async () => {
			try {
				const eligible = await Surveys.getEligible()
				if (cancelled) return
				setActive(pickSurvey(eligible, path))
			} catch (err) {
				if (!cancelled) setActive(null)
			}
		})()

		return () => {
			cancelled = true
		}
	}, [location.pathname, user?.id])

	// Record a single impression when a survey becomes visible (drives cool-off).
	useEffect(() => {
		if (active && impressionSent.current !== active.id) {
			impressionSent.current = active.id
			Surveys.impression(active.id, account?.id || '').catch(() => undefined)
		}
	}, [active, account?.id])

	if (!active) {
		return null
	}

	const getFollowUp = (score: number): string => {
		if (score <= 3) return active.branch_prompts.low
		if (score === 4) return active.branch_prompts.four
		return active.branch_prompts.five
	}

	const handleSubmit = (result: FeedbackSurveyResult) => {
		const survey = active
		Surveys.submitResponse(survey.id, {
			rating: result.score,
			feedback: result.comment || '',
			account_id: account?.id || ''
		}).catch(() => undefined)
	}

	const handleClose = () => {
		const survey = active
		setActive(null)
		Surveys.dismiss(survey.id, account?.id || '').catch(() => undefined)
	}

	return (
		<div className="absolute bottom-7 left-7 z-50 max-w-sm">
			<FeedbackSurvey
				key={active.id}
				question={active.rating_question}
				scaleMax={5}
				lowLabel={active.scale_labels.min}
				highLabel={active.scale_labels.max}
				getFollowUp={getFollowUp}
				placeholder="Optional"
				submitLabel="Submit feedback"
				onSubmit={handleSubmit}
				onClose={handleClose}
			/>
		</div>
	)
}
